import { Box, Card, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { ReactNode } from 'react';
import { usePanelTokens } from './tokens';

export interface PanelCardProps {
  /** Card heading (small, muted-strong). Omit for a chrome-less container. */
  title?: ReactNode;
  /** Optional info tooltip shown next to the title. */
  info?: string;
  /** Right-aligned slot in the header (e.g. a period chip or action). */
  action?: ReactNode;
  children: ReactNode;
  /** Extra sx merged onto the Card root. */
  sx?: Record<string, unknown>;
  /** Minimal/flat is the default; pass false to inherit the global card style. */
  flat?: boolean;
  /** Optional click handler — adds pointer cursor when provided. */
  onClick?: () => void;
}

/**
 * The base card for every AnooreHR Panel widget. Carries the flat/minimal
 * aesthetic locally (10px radius, no shadow, hairline border, airy padding) so
 * the panel widgets look right regardless of the app-wide MuiCard default.
 */
export function PanelCard({ title, info, action, children, sx, flat = true, onClick }: PanelCardProps) {
  const t = usePanelTokens();
  return (
    <Card
      onClick={onClick}
      sx={{
        p: 2.5,
        borderRadius: flat ? '10px' : undefined,
        boxShadow: flat ? 'none' : undefined,
        border: `1px solid ${t.border}`,
        height: '100%',
        '&:hover': flat
          ? { borderColor: t.isDark ? '#3D414B' : '#C7CBD4', boxShadow: 'none' }
          : undefined,
        ...sx,
      }}
    >
      {(title || action) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2 }}>
          {title && (
            <Typography
              sx={{ fontSize: '0.8125rem', fontWeight: 700, color: t.label, letterSpacing: 0 }}
            >
              {title}
            </Typography>
          )}
          {info && (
            <Tooltip title={info} arrow placement="top">
              <InfoOutlinedIcon sx={{ fontSize: 15, color: t.label, cursor: 'help' }} />
            </Tooltip>
          )}
          {action && <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>{action}</Box>}
        </Box>
      )}
      {children}
    </Card>
  );
}

/** Two-value header: accent value + label, divider, total value + label. */
export function StatHeader({
  usedValue,
  usedLabel,
  totalValue,
  totalLabel,
  usedColor,
}: {
  usedValue: ReactNode;
  usedLabel: ReactNode;
  totalValue?: ReactNode;
  totalLabel?: ReactNode;
  usedColor?: string;
}) {
  const t = usePanelTokens();
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.75 }}>
      <Box>
        <Typography
          sx={{ fontSize: '1.4rem', fontWeight: 800, lineHeight: 1, letterSpacing: '-0.02em', color: usedColor ?? t.a }}
        >
          {usedValue}
        </Typography>
        <Typography sx={{ fontSize: '0.6875rem', color: t.label, mt: 0.6, fontWeight: 500 }}>
          {usedLabel}
        </Typography>
      </Box>
      {totalValue != null && (
        <>
          <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: t.border, my: 0.3 }} />
          <Box>
            <Typography
              sx={{ fontSize: '1.4rem', fontWeight: 800, lineHeight: 1, letterSpacing: '-0.02em', color: t.ink }}
            >
              {totalValue}
            </Typography>
            <Typography sx={{ fontSize: '0.6875rem', color: t.label, mt: 0.6, fontWeight: 500 }}>
              {totalLabel}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
}
